import redirects from '@zg-rentals/auth-comet/redirects';
import type { Route } from 'nextjs-routes';

const BASE_ROUTE = '/renter-hub/applications';

export const EXTERNAL_URLS = {
  RENTALS_SEARCH_PAGE_URL:
    '/homes/for_rent/?searchQueryState=%7B%22pagination%22%3A%7B%7D%2C%22isMapVisible%22%3Atrue%2C%22filterState%22%3A%7B%22fr%22%3A%7B%22value%22%3Atrue%7D%2C%22fsba%22%3A%7B%22value%22%3Afalse%7D%2C%22fsbo%22%3A%7B%22value%22%3Afalse%7D%2C%22nc%22%3A%7B%22value%22%3Afalse%7D%2C%22cmsn%22%3A%7B%22value%22%3Afalse%7D%2C%22auc%22%3A%7B%22value%22%3Afalse%7D%2C%22fore%22%3A%7B%22value%22%3Afalse%7D%2C%22app%22%3A%7B%22value%22%3Atrue%7D%7D%2C%22isListVisible%22%3Atrue%2C%22mapZoom%22%3A15%7D',
  PROFILE: `/myzillow/profile`,
  CONVERSATION: '/renter-hub/inbox/',
  SIGN_IN: redirects.zillow.signInPage(
    BASE_ROUTE,
    '?he=Continue to Applications&sh=Sign in to securely manage your rental applications.',
  ),
};

// if not local, use relative URLs
export const BASE_URL = process.env.NEXT_PUBLIC_LOCAL_BASE_URL ?? '';
export const GRAPHQL_BASE_URL = process.env.NEXT_PUBLIC_LOCAL_GRAPHQL_BASE_URL ?? BASE_URL;

export function getExternalUrl(path: string) {
  return `${BASE_URL}${path}`;
}

export {
  APPLICATIONS_FAQ_URL,
  ASSISTANCE_ANIMALS_URL,
  CO_APPLICANT_FAQ_URL,
  CONDITIONAL_APPROVAL_URL,
  FAIR_HOUSING_URL,
  PORTLAND_APPLICANT_RIGHTS_URL,
  PORTLAND_MODIFICATION_ACCOMMODATION_URL,
  RENTAL_APPLICATION_REPORTS_LAWS_URL,
  SUPPORT_REQUEST_URL,
  TERMS_OF_USE_URL,
  TERMS_RENTER_URL,
} from '@zg-rentals/applications-utils';

export const GRAPHQL_ENDPOINT = process.env.NEXT_PUBLIC_LOCAL_GRAPHQL_ENDPOINT ?? '/zg-graph';

export const ROUTES = {
  INDEX: BASE_ROUTE,
  APPLICATION: `/application/:applicationId`,
  CREATE: `/listing/:listingAlias/rental-application`,
} as const;

export const API = {
  SEND_CONVERSATION_MESSAGE: '/renter-hub-api/api/v1/users/capps/sendConversationMessage',
  GET_CONVERSATION_INFO_IF_EXISTS: '/renter-hub-api/api/v1/users/listingConversation/getConversationInfoIfExists',
  GET_ADDRESS_AUTOCOMPLETE: '/renter-hub-api/api/v1/addresses/autocomplete',
};

export function getBaseApplicationRoute(applicationId: string, route: string) {
  return route.replace(':applicationId', applicationId);
}

export function getApplicationRoute({ applicationId }: { applicationId: string }): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: '/application/[applicationId]',
    query: {
      applicationId,
    },
  };
}

export function getApplicationRouteForInvite({
  listingAlias,
  rentalApplicationInviteId,
}: {
  listingAlias: string;
  rentalApplicationInviteId: string;
}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: '/listing/[listingAlias]/rental-application',
    query: {
      listingAlias,
      inviteId: rentalApplicationInviteId,
      version: '1',
    },
  };
}

export function getEditRoute({
  applicationId,
  stepId,
  nextStepId,
  reportType,
  onlyIncomplete,
}: {
  applicationId: string;
  stepId?: string;
  nextStepId?: string;
  reportType?: string;
  onlyIncomplete?: string;
}): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  const query: Route['query'] = {
    applicationId,
  };

  if (stepId) {
    query.stepId = stepId;
  }

  if (nextStepId) {
    query.nextStepId = nextStepId;
  }

  if (reportType) {
    query.reportType = reportType;
  }

  if (onlyIncomplete) {
    // onlyIncomplete will navigate to the next incomplete step when clicking "Next" instead of the nextStepId
    // If no incompletes, it will go to the review
    query.onlyIncomplete = onlyIncomplete;
  }

  return {
    pathname: '/application/[applicationId]',
    query,
  };
}

export function getCreateRoute(listingAlias: string): {
  pathname: Route['pathname'];
  query: Route['query'];
} {
  return {
    pathname: '/listing/[listingAlias]/rental-application',
    query: {
      listingAlias,
    },
  };
}
